// src/pages/LoginSignup.js
import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../msalConfig";
import { UserContext } from "../UserContext";
import "../styles/loginSignup.css";

/* =====================================================
   Configuration (DO NOT BREAK IN DEV)
===================================================== */

// DEV is determined by email domain — bullet‑proof
const isDevEmail = (email) =>
  typeof email === "string" && email.endsWith("@dev.edgeneweb");

// Backend API — never default to :3000
const API_BASE =
  process.env.REACT_APP_API_BASE || "http://localhost:5000";

/* =====================================================
   DEV USERS (frontend‑only mock authentication)
===================================================== */
const DEV_USERS = {
  "admin@dev.edgeneweb": {
    name: "Dev",
    surname: "Admin",
    role: "admin",
  },
  "user@dev.edgeneweb": {
    name: "Dev",
    surname: "User",
    role: "user",
  },
  "student@dev.edgeneweb": {
    name: "Dev",
    surname: "Student",
    role: "student",
  },
};

function LoginSignup() {
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const { login } = useContext(UserContext);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  /* =====================================================
     MSAL Redirect Handling (PROD ONLY)
     DEV can NEVER reach this path
  ===================================================== */
  useEffect(() => {
    if (!accounts || accounts.length === 0) return;

    const account = accounts[0];
    const fullName = account.name || "";
    const [name, ...rest] = fullName.split(" ");
    const surname = rest.join(" ");

    login(account.username, name || "User", surname || "");
    navigate("/checkout");
  }, [accounts, login, navigate]);

  /* =====================================================
     Credential Login
===================================================== */
  const handleCredentialLogin = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    /* ---------------- DEV LOGIN (NO NETWORK EVER) ---------------- */
    if (isDevEmail(email)) {
      const devUser = DEV_USERS[email];

      if (!devUser || !password.startsWith("dev-")) {
        setError("Invalid DEV credentials");
        setIsLoading(false);
        return;
      }

      login(email, devUser.name, devUser.surname, devUser.role);
      navigate("/checkout");
      return; // 🚫 HARD STOP — no fall‑through
    }

    /* ---------------- PROD LOGIN (API) ---------------- */
    try {
      const res = await fetch(`${API_BASE}/api/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Invalid credentials");
      }

      const data = await res.json();
      login(data.email, data.name, data.surname);
      navigate("/checkout");
    } catch (err) {
      setError(err.message || "Login failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  /* =====================================================
     Microsoft Login (STRICTLY PROD)
===================================================== */
  const handleMicrosoftLogin = () => {
    // DEV must never touch Azure
    if (isDevEmail(email)) {
      setError("Microsoft login is disabled in DEV mode.");
      return;
    }

    setIsLoading(true);
    setError("");

    instance.loginRedirect(loginRequest).catch((err) => {
      console.error(err);
      setError("Microsoft login failed.");
      setIsLoading(false);
    });
  };

  /* =====================================================
     Render
===================================================== */
  return (
    <div className="login-signup-container">
      <h2>Login</h2>

      {isDevEmail(email) && (
        <div
          style={{
            padding: "10px",
            background: "#ffcc00",
            color: "#000",
            fontWeight: "bold",
            marginBottom: "12px",
            textAlign: "center",
          }}
        >
          DEV MODE — mock authentication
        </div>
      )}

      <div className="auth-form">
        <form onSubmit={handleCredentialLogin}>
          <input
            type="email"
            placeholder="Email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />

          <input
            type="password"
            placeholder="Password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />

          {error && <div className="error-message">{error}</div>}

          <button type="submit" disabled={isLoading}>
            {isLoading ? "Please wait…" : "Login"}
          </button>
        </form>

        {!isDevEmail(email) && (
          <>
            <div className="divider">or</div>
            <button onClick={handleMicrosoftLogin}>
              Login with Microsoft
            </button>
          </>
        )}
      </div>

      <div className="switch-auth">
        <p>
          Don&apos;t have an account?{" "}
          <a href="/signup">Sign up</a>
        </p>
      </div>
    </div>
  );
}

export default LoginSignup;